// frontend/src/components/leads/LeadTable.js

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import LeadStatusBadge from "./LeadStatusBadge";
import { getUserTimezone, formatDateInUserTimezone ,formatUtcDateTimeParts} from '../../utils/timezone';
import api from "../../services/api";
import AddActivity from "../activities/AddActivity";

function LeadTable({
  leads,
  onBookDemo,
  onView,
  onEdit,
  onLeadQuickUpdated,
  onLeadProductsQuickUpdated,
  onAddActivity,
  userRole,
  visibleColumns = {},
  productCatalog = [],
}) {
  const [overlay, setOverlay] = useState(null);

  const [quickEditLeadId, setQuickEditLeadId] = useState(null);
  const [quickEditStatus, setQuickEditStatus] = useState("");
  const [statusOptions, setStatusOptions] = useState([]);
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [quickEditPosition, setQuickEditPosition] = useState(null);

  const [quickEditProductsLeadId, setQuickEditProductsLeadId] = useState(null);
  const [quickEditProducts, setQuickEditProducts] = useState([]);
  const [isSavingProducts, setIsSavingProducts] = useState(false);
  const [productsError, setProductsError] = useState("");
  const [productsQuickEditPosition, setProductsQuickEditPosition] =
    useState(null);

  const [activityLeadId, setActivityLeadId] = useState(null);
  const [isSavingActivity, setIsSavingActivity] = useState(false);

  const quickEditMenuRef = useRef(null);
  const productsQuickEditMenuRef = useRef(null);
  
  const userTimezone = getUserTimezone();
  
  useEffect(() => {
    let cancelled = false;

    api
      .getMasterData("leadStatuses")
      .then((values) => {
        if (!cancelled) {
          setStatusOptions(Array.isArray(values) ? values : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatusOptions([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!quickEditLeadId) return;

    const handleClickOutside = (event) => {
      if (!event.target.closest(".lead-status-cell")) {
        setQuickEditLeadId(null);
        setStatusError("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [quickEditLeadId]);

  useEffect(() => {
    if (!quickEditProductsLeadId) return;

    const handleProductsClickOutside = (event) => {
      if (!event.target.closest(".lead-products-cell")) {
        setQuickEditProductsLeadId(null);
        setProductsError("");
      }
    };

    document.addEventListener("mousedown", handleProductsClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleProductsClickOutside);
    };
  }, [quickEditProductsLeadId]);

  const openStatusQuickEdit = (lead, event) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();

    setQuickEditPosition({
      top: rect.bottom + 6,
      left: Math.max(8, rect.left - 100),
      anchorTop: rect.top,
      anchorBottom: rect.bottom,
    });

    setQuickEditLeadId(lead.lead_id);
    setQuickEditStatus(lead.status || "");
    setStatusError("");
  };

  const openProductsQuickEdit = (lead, event) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();

    setProductsQuickEditPosition({
      top: rect.bottom + 6,
      left: Math.max(8, rect.left - 100),
      anchorTop: rect.top,
      anchorBottom: rect.bottom,
    });

    setQuickEditProductsLeadId(lead.lead_id);
    setQuickEditProducts(normalizeList(lead.products));
    setProductsError("");
  };

  useEffect(() => {
    if (!quickEditLeadId || !quickEditPosition || !quickEditMenuRef.current) {
      return;
    }

    const menu = quickEditMenuRef.current;
    const menuRect = menu.getBoundingClientRect();

    const spaceBelow = window.innerHeight - quickEditPosition.anchorBottom;

    const spaceAbove = quickEditPosition.anchorTop;

    const gap = 6;

    if (spaceBelow >= menuRect.height + gap) {
      setQuickEditPosition((current) => ({
        ...current,
        top: current.anchorBottom + gap,
      }));
    } else if (spaceAbove >= menuRect.height + gap) {
      setQuickEditPosition((current) => ({
        ...current,
        top: current.anchorTop - menuRect.height - gap,
      }));
    }
  }, [
    quickEditLeadId,
    quickEditPosition?.anchorTop,
    quickEditPosition?.anchorBottom,
  ]);

  useEffect(() => {
    if (
      !quickEditProductsLeadId ||
      !productsQuickEditPosition ||
      !productsQuickEditMenuRef.current
    ) {
      return;
    }

    const menu = productsQuickEditMenuRef.current;
    const menuRect = menu.getBoundingClientRect();

    const gap = 6;
    const minTop = 90;
    const bottomMargin = 16;

    const spaceBelow =
      window.innerHeight - productsQuickEditPosition.anchorBottom;

    const spaceAbove = productsQuickEditPosition.anchorTop - minTop;

    if (spaceBelow >= menuRect.height + gap) {
      // Enough room below → open downward
      setProductsQuickEditPosition((current) => ({
        ...current,
        top: current.anchorBottom + gap,
      }));
    } else if (spaceAbove >= menuRect.height + gap) {
      // Enough room above → open upward
      setProductsQuickEditPosition((current) => ({
        ...current,
        top: Math.max(minTop, current.anchorTop - menuRect.height - gap),
      }));
    } else {
      // Not enough room either way → keep the whole popup inside the viewport
      const maxTop = Math.max(
        minTop,
        window.innerHeight - menuRect.height - bottomMargin,
      );

      setProductsQuickEditPosition((current) => ({
        ...current,
        top: Math.min(Math.max(minTop, current.anchorBottom + gap), maxTop),
      }));
    }
  }, [
    quickEditProductsLeadId,
    productsQuickEditPosition?.anchorTop,
    productsQuickEditPosition?.anchorBottom,
  ]);

  const handleStatusQuickSave = async (lead) => {
    if (!quickEditStatus || quickEditStatus === lead.status) {
      setQuickEditLeadId(null);
      return;
    }

    setIsSavingStatus(true);
    setStatusError("");

    try {
      await api.updateLead(lead.lead_id, {
        status: quickEditStatus,
      });

      if (onLeadQuickUpdated) {
        onLeadQuickUpdated(lead.lead_id, quickEditStatus);
      }

      setQuickEditLeadId(null);
    } catch (err) {
      setStatusError(
        err?.response?.data?.message || "Failed to update status.",
      );
    } finally {
      setIsSavingStatus(false);
    }
  };

  const handleProductsQuickSave = async (lead) => {
    const currentProducts = normalizeList(lead.products);

    if (JSON.stringify(quickEditProducts) === JSON.stringify(currentProducts)) {
      setQuickEditProductsLeadId(null);
      return;
    }

    setIsSavingProducts(true);
    setProductsError("");

    try {
      await api.updateLead(lead.lead_id, {
        products: quickEditProducts,
      });

      if (onLeadProductsQuickUpdated) {
        onLeadProductsQuickUpdated(lead.lead_id, quickEditProducts);
      }

      setQuickEditProductsLeadId(null);
    } catch (err) {
      setProductsError(
        err?.response?.data?.message || "Failed to update products.",
      );
    } finally {
      setIsSavingProducts(false);
    }
  };

  if (!Array.isArray(leads) || leads.length === 0) {
    return (
      <div className="empty-state partner-empty-state">
        <i className="fas fa-inbox"></i>
        <h3>No leads found</h3>
        <p>Try changing your filters or add a new lead.</p>
      </div>
    );
  }

  const show = (column) => visibleColumns[column] !== false;
  const ownerCanSeeSalesRep = [
    "Admin",
    "SM",
    "Sales Owner",
    "Sales Manager",
  ].includes(String(userRole || "").trim());

  const groupedProductCatalog = productCatalog.reduce((groups, product) => {
    const value = product?.name || product?.product_name;

    if (!value) return groups;

    const category = product?.category || product?.category_name || "Other";

    if (!groups[category]) {
      groups[category] = [];
    }

    groups[category].push(product);

    return groups;
  }, {});

  const normalizeList = (value) => {
    if (Array.isArray(value))
      return value.filter(
        (v) => v !== null && v !== undefined && String(v).trim(),
      );
    if (typeof value === "string" && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [value];
      } catch {
        return [value];
      }
    }
    return [];
  };

  const openOverlay = (title, items, lead) => {
    if (title === "Products" && lead) {
      setOverlay({
        title: "Products",
        items: items,
        lead: lead,
        type: "products",
      });
    } else {
      setOverlay({ title, items, type: "list" });
    }
  };

  const getProductCategory = (productName) => {
    if (!productCatalog || !Array.isArray(productCatalog)) return "";
    const found = productCatalog.find(
      (p) => p.name === productName || p.product_name === productName,
    );
    return found?.category || found?.category_name || "";
  };

  const allowedActivityTypes = [
    "Demo",
    "Email",
    "Follow-up",
    "Meeting",
    "Note Added",
    "Phone Call",
    "Status Changed",
  ];

  return (
    <>
      <div className="partner-table-wrapper">
        <table className="partner-leads-table">
          <thead>
            <tr>
              {show("dealership") && <th>Dealership</th>}
              {show("salesRep") && ownerCanSeeSalesRep && <th>Sales Rep</th>}
              {show("contact") && <th>Contact</th>}
              {show("status") && <th>Status</th>}
              {show("products") && <th>Products</th>}
              {show("activity") && <th>Activity</th>}
              {show("lastContact") && <th>Last Contact</th>}
              {show("demo") && <th>Demo</th>}
              <th className="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((lead) => {
              const products = normalizeList(lead.products);
             const latestActivity = Array.isArray(lead.activities)? lead.activities[0] || null : null;
              const demoStatus =
                lead.demo_status || lead.demoStatus || "Not Booked";
              const demoDate = lead.demo_date || lead.demoDate || "";
              const demoTime = lead.demo_time || lead.demoTime || "";
              const demoDateTime =
                demoDate || demoTime
                  ? formatUtcDateTimeParts(demoDate, demoTime)
                  : "";
              const firstContact = Array.isArray(lead.contacts)
                ? lead.contacts[0]
                : null;
              const contactName =
                [
                  firstContact?.first_name || firstContact?.firstName,
                  firstContact?.last_name || firstContact?.lastName,
                ]
                  .filter(Boolean)
                  .join(" ") ||
                lead.contact_name ||
                "";
              const contactEmail =
                firstContact?.email || lead.contact_email || "";

              return (
                <tr key={lead.lead_id}>
                  {show("dealership") && (
                    <td data-label="Dealership" className="lead-company-cell">
                      <button
                        type="button"
                        className="partner-lead-link"
                        onClick={() => onView(lead.lead_id)}
                      >
                        {lead.company || "Unnamed Dealership"}
                      </button>
                      {lead.email && (
                        <div className="lead-secondary-text">{lead.email}</div>
                      )}
                    </td>
                  )}
                  {show("salesRep") && ownerCanSeeSalesRep && (
                    <td data-label="Sales Rep">
                      <div className="sales-rep-cell">
                        <span className="sales-rep-avatar">
                          {getInitials(lead.partner_name)}
                        </span>
                        <span>
                          {lead.partner_name || lead.sales_rep_name || "N/A"}
                        </span>
                      </div>
                    </td>
                  )}
                  {show("contact") && (
                    <td data-label="Contact">
                      {contactName ? (
                        <>
                          <div>{contactName}</div>
                          {contactEmail && (
                            <div className="lead-secondary-text">
                              {contactEmail}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="lead-secondary-text">N/A</span>
                      )}
                    </td>
                  )}
                  {show("status") && (
                    <td data-label="Status" className="lead-status-table-cell">
                      <div className="lead-status-cell">
                        <LeadStatusBadge status={lead.status} />

                        <button
                          type="button"
                          className="lead-quick-edit-button"
                          onClick={(event) => openStatusQuickEdit(lead, event)}
                          title="Edit status"
                          aria-label={`Edit status for ${lead.lead_id}`}
                        >
                          <i className="fas fa-pencil-alt"></i>
                        </button>

                        {quickEditLeadId === lead.lead_id &&
                          quickEditPosition &&
                          createPortal(
                            <>
                              <div
                                className="lead-quick-edit-backdrop"
                                onMouseDown={() => {
                                  setQuickEditLeadId(null);
                                  setStatusError("");
                                }}
                              />

                              <div
                                ref={quickEditMenuRef}
                                className="lead-quick-edit-menu"
                                style={{
                                  position: "fixed",
                                  top: `${quickEditPosition.top}px`,
                                  left: `${quickEditPosition.left}px`,
                                }}
                                onMouseDown={(event) => event.stopPropagation()}
                              >
                                <div className="lead-quick-edit-title">
                                  Edit Status
                                </div>

                                <div className="lead-quick-edit-options">
                                  <select
                                    className="lead-quick-edit-select"
                                    value={quickEditStatus}
                                    onChange={(event) =>
                                      setQuickEditStatus(event.target.value)
                                    }
                                  >
                                    {statusOptions.map((option) => {
                                      const value =
                                        typeof option === "string"
                                          ? option
                                          : option?.name;

                                      if (!value) return null;

                                      return (
                                        <option key={value} value={value}>
                                          {value}
                                        </option>
                                      );
                                    })}
                                  </select>
                                </div>

                                {statusError && (
                                  <div className="lead-quick-edit-error">
                                    {statusError}
                                  </div>
                                )}

                                <div className="lead-quick-edit-actions">
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => setQuickEditLeadId(null)}
                                    disabled={isSavingStatus}
                                  >
                                    Cancel
                                  </button>

                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleStatusQuickSave(lead)}
                                    disabled={
                                      isSavingStatus || !quickEditStatus
                                    }
                                  >
                                    {isSavingStatus ? "Saving..." : "Save"}
                                  </button>
                                </div>
                              </div>
                            </>,
                            document.body,
                          )}
                      </div>
                    </td>
                  )}
                  {show("products") && (
                    <td
                      data-label="Products"
                      className="lead-products-table-cell"
                    >
                      <div className="lead-products-cell">
                        {products.length ? (
                          <div className="partner-table-tags">
                            {products.slice(0, 1).map((product, i) => (
                              <span
                                className="partner-table-tag lead-product-name"
                                key={`${product}-${i}`}
                                title={product}
                              >
                                {product}
                              </span>
                            ))}

                            {products.length > 1 && (
                              <button
                                type="button"
                                className="partner-table-more"
                                onClick={() =>
                                  openOverlay("Products", products, lead)
                                }
                                title="View all products"
                              >
                                +{products.length - 1}
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="lead-secondary-text">None</span>
                        )}

                        <button
                          type="button"
                          className="lead-quick-edit-button"
                          onClick={(event) =>
                            openProductsQuickEdit(lead, event)
                          }
                          title="Edit products"
                          aria-label={`Edit products for ${lead.lead_id}`}
                        >
                          <i className="fas fa-pencil-alt"></i>
                        </button>
                        {quickEditProductsLeadId === lead.lead_id &&
                          productsQuickEditPosition &&
                          createPortal(
                            <>
                              <div
                                className="lead-quick-edit-backdrop"
                                onMouseDown={() => {
                                  setQuickEditProductsLeadId(null);
                                  setProductsError("");
                                }}
                              />

                              <div
                                ref={productsQuickEditMenuRef}
                                className="lead-quick-edit-menu lead-products-quick-edit-menu"
                                style={{
                                  position: "fixed",
                                  top: `${productsQuickEditPosition.top}px`,
                                  left: `${productsQuickEditPosition.left}px`,
                                }}
                                onMouseDown={(event) => event.stopPropagation()}
                              >
                                <div className="lead-products-quick-edit-header">
                                  <div className="lead-quick-edit-title">
                                    Edit Products
                                    {quickEditProducts.length > 0 && (
                                      <span className="lead-products-selected-count">
                                        {quickEditProducts.length} selected
                                      </span>
                                    )}
                                  </div>

                                  {quickEditProducts.length > 0 && (
                                    <button
                                      type="button"
                                      className="lead-products-quick-edit-clear"
                                      onClick={() => setQuickEditProducts([])}
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>

                                <div className="lead-products-quick-edit-list">
                                  {Object.entries(groupedProductCatalog).map(
                                    ([category, products]) => (
                                      <div
                                        key={category}
                                        className="product-category-group"
                                      >
                                        <div className="product-category-label">
                                          {category}
                                        </div>

                                        <div className="product-category-products">
                                          {products.map((product) => {
                                            const value =
                                              product?.name ||
                                              product?.product_name;

                                            const checked =
                                              quickEditProducts.includes(value);

                                            return (
                                              <label
                                                key={value}
                                                className="filter-checkbox-option"
                                              >
                                                <input
                                                  type="checkbox"
                                                  checked={checked}
                                                  onChange={(event) => {
                                                    setQuickEditProducts(
                                                      (current) =>
                                                        event.target.checked
                                                          ? [...current, value]
                                                          : current.filter(
                                                              (item) =>
                                                                item !== value,
                                                            ),
                                                    );
                                                  }}
                                                />

                                                <span>{value}</span>

                                                {product?.subcategory && (
                                                  <small>
                                                    {product.subcategory}
                                                  </small>
                                                )}
                                              </label>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    ),
                                  )}
                                </div>

                                {productsError && (
                                  <div className="lead-quick-edit-error">
                                    {productsError}
                                  </div>
                                )}

                                <div className="lead-quick-edit-actions">
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() =>
                                      setQuickEditProductsLeadId(null)
                                    }
                                    disabled={isSavingProducts}
                                  >
                                    Cancel
                                  </button>

                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() =>
                                      handleProductsQuickSave(lead)
                                    }
                                    disabled={isSavingProducts}
                                  >
                                    {isSavingProducts ? "Saving..." : "Save"}
                                  </button>
                                </div>
                              </div>
                            </>,
                            document.body,
                          )}
                      </div>
                    </td>
                  )}
                  {show("activity") && (
                    <td data-label="Activity">
                      {latestActivity?.type ? (
                        <span>{latestActivity.type}</span>
                      ) : (
                        <span className="lead-secondary-text">N/A</span>
                      )}
                    </td>
                  )}

                  {show("lastContact") && (
                    <td data-label="Last Contact">
                      {latestActivity?.created_at ? (
                        <span>
                          {formatDateInUserTimezone(latestActivity.created_at,userTimezone)}
                        </span>
                      ) : (
                        <span className="lead-secondary-text">N/A</span>
                      )}
                    </td>
                  )}
                  {show("demo") && (
                    <td data-label="Demo">
                      {demoStatus === "Not Booked" ? (
                        <button
                          type="button"
                          className="book-demo-button lead-table-book-demo"
                          onClick={() => onBookDemo(lead.lead_id)}
                          title="Book Demo"
                        >
                          Book demo
                        </button>
                      ) : (
                        <>
                          <span
                            className={`demo-status-${String(demoStatus).toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {demoStatus}
                          </span>

                          {demoDateTime && (
                            <div className="lead-secondary-text">
                              {demoDateTime}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  )}
                  <td data-label="Actions" className="actions">
                    <div className="partner-row-actions">
                      <button
                        type="button"
                        className="partner-icon-action"
                        onClick={() => onEdit(lead.lead_id)}
                        title="Edit Lead"
                        aria-label="Edit Lead"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        type="button"
                        className="partner-icon-action"
                        onClick={() => setActivityLeadId(lead.lead_id)}
                        title="Add Activity"
                        aria-label="Add Activity"
                      >
                        <i className="fas fa-plus-circle"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ============================================================
          PRODUCTS MODAL - Like Partner Site
          ============================================================ */}
      {overlay && (
        <div
          className="modal-overlay"
          role="presentation"
          onMouseDown={() => setOverlay(null)}
        >
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label={overlay.title}
            style={{
              maxWidth: overlay.type === "products" ? "480px" : "520px",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>{overlay.title}</h3>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setOverlay(null)}
                aria-label="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              {overlay.type === "products" ? (
                <div className="products-modal-grid">
                  {overlay.items.map((item, i) => {
                    const category = getProductCategory(item);
                    return (
                      <div key={`${item}-${i}`} className="product-modal-item">
                        <span className="product-modal-name">{item}</span>
                        {category && (
                          <span className="product-modal-category">
                            {category}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <ul className="modal-list">
                  {overlay.items.map((item, i) => (
                    <li key={`${item}-${i}`}>{item}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setOverlay(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {activityLeadId &&
        createPortal(
          <div
            className="modal-overlay"
            role="presentation"
            onMouseDown={() => setActivityLeadId(null)}
          >
            <div
              className="modal"
              role="dialog"
              aria-modal="true"
              aria-label="Add Activity"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Add Activity</h3>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setActivityLeadId(null)}
                  aria-label="Close"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="modal-body">
                <AddActivity
                  onSave={async (activityData) => {
                    setIsSavingActivity(true);

                    try {
                      await onAddActivity(activityLeadId, activityData);
                      setActivityLeadId(null);
                    } finally {
                      setIsSavingActivity(false);
                    }
                  }}
                  onCancel={() => setActivityLeadId(null)}
                  saving={isSavingActivity}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function getInitials(name) {
  if (!name) return "?";
  return String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

export default LeadTable;
